import React from 'react';
import { Check, X, AlertCircle } from 'lucide-react';

// Steps in a row: done ones ticked, the current one highlighted, later ones muted. Used by the status
// card's progress tracker and by the form wizards. `steps` is [{ label, state }] with state
// 'done' | 'current' | 'attention' | 'failed' | 'upcoming' (see trackerSteps / wizardSteps).
const CIRCLE = {
    done: 'bg-portal-green border-portal-green text-white',
    current: 'bg-portal-purple border-portal-purple text-white ring-4 ring-portal-purple-light',
    attention: 'bg-portal-orange border-portal-orange text-white ring-4 ring-portal-orange-light',
    failed: 'bg-red-600 border-red-600 text-white',
    upcoming: 'bg-white border-portal-border text-portal-sub',
};

const LABEL = {
    done: 'text-portal-ink font-semibold',
    current: 'text-portal-purple-dark font-bold',
    attention: 'text-portal-orange-ink font-bold',
    failed: 'text-red-700 font-bold',
    upcoming: 'text-portal-sub',
};

// Read after each label, since the colours and icons say it only visually.
const SPOKEN_STATE = {
    done: 'completed',
    current: 'current step',
    attention: 'needs your attention',
    failed: 'not approved',
    upcoming: 'not started',
};

const Stepper = ({ steps, label, className = '' }) => (
    <ol className={`flex items-start ${className}`} aria-label={label}>
        {steps.map(({ label: stepLabel, state }, index) => (
            <li
                key={`${index}-${stepLabel}`}
                data-state={state}
                aria-current={state === 'current' || state === 'attention' ? 'step' : undefined}
                className="relative flex-1 min-w-0 flex flex-col items-center text-center"
            >
                {index < steps.length - 1 && (
                    <span aria-hidden="true" className={`absolute top-3 left-1/2 w-full h-0.5 ${state === 'done' ? 'bg-portal-green' : 'bg-portal-border'}`} />
                )}
                <span aria-hidden="true" className={`relative w-6 h-6 rounded-full border-2 flex items-center justify-center text-[11px] font-bold ${CIRCLE[state] || CIRCLE.upcoming}`}>
                    {state === 'done' ? <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        : state === 'failed' ? <X className="w-3.5 h-3.5" strokeWidth={3} />
                            : state === 'attention' ? <AlertCircle className="w-3.5 h-3.5" />
                                : index + 1}
                </span>
                <span className={`mt-1.5 px-0.5 text-[11px] leading-tight break-words ${LABEL[state] || LABEL.upcoming}`}>
                    {stepLabel}
                    <span className="sr-only">, {SPOKEN_STATE[state] || SPOKEN_STATE.upcoming}</span>
                </span>
            </li>
        ))}
    </ol>
);

export default Stepper;
